import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule, UrlTree } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';

@Component({
    selector: 'app-menu',
    imports: [MenuModule, BadgeModule, RouterModule, RippleModule],
    styles: [
        `
            .layout-menu a.p-menu-item-link.active {
                color: var(--primary-color);
                font-weight: 600;
            }
        `
    ],
    template: `<div class="layout-menu">
        <p-menu [model]="model()" [expandedKeys]="expandedKeys()" (expandedKeysChange)="openItems.set($event ?? {})">
            <ng-template #submenuheader let-item>
                <span class="font-bold">{{ item.label }}</span>
            </ng-template>
            <ng-template #item let-item>
                <a
                    pRipple
                    class="flex items-center p-menu-item-link"
                    [routerLink]="item.routerLink"
                    [queryParams]="item.queryParams"
                    routerLinkActive="active"
                    [routerLinkActiveOptions]="item.routerLinkActiveOptions || { exact: true }"
                    ariaCurrentWhenActive="page"
                    tabindex="-1"
                >
                    <span [class]="item.icon"></span>
                    <span class="ml-2">{{ item.label }}</span>
                    @if (item.badge) {
                        <p-badge class="ml-auto" [value]="item.badge" [severity]="item.badgeStyleClass" />
                    }
                    @if (item.shortcut) {
                        <span class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1">{{ item.shortcut }}</span>
                    }
                    @if (item.items?.length) {
                        <i class="pi ml-auto" [class.pi-chevron-down]="expandedKeys()[item.id]" [class.pi-chevron-right]="!expandedKeys()[item.id]" aria-hidden="true"></i>
                    }
                </a>
            </ng-template>
        </p-menu>
    </div>`
})
export class AppMenu {
    private readonly router = inject(Router);
    private readonly navigationEnd = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));
    readonly menuItems = input<MenuItem[]>([]);
    readonly openItems = signal<Record<string, boolean>>({});
    readonly model = computed(() => this.withIds(this.menuItems()));
    readonly expandedKeys = computed(() => {
        this.navigationEnd();
        const keys = { ...this.openItems() };
        const visit = (items: MenuItem[]) => {
            for (const item of items) {
                if (item.items?.length) {
                    if (item.id && this.hasActiveDescendant(item)) keys[item.id] = true;
                    visit(item.items);
                }
            }
        };
        visit(this.model());
        return keys;
    });

    private withIds(items: MenuItem[], parentId = 'sidebar-menu'): MenuItem[] {
        return items.map((item, index) => {
            const id = item.id ?? `${parentId}-${index}`;
            // PrimeNG mutates expansion flags; keep the input model owned by the layout immutable.
            return { ...item, id, items: item.items ? this.withIds(item.items, id) : undefined };
        });
    }

    private hasActiveDescendant(item: MenuItem): boolean {
        return !!item.items?.some((child) => child.visible !== false && (this.isItemActive(child) || this.hasActiveDescendant(child)));
    }

    private isItemActive(item: MenuItem): boolean {
        if (!item.routerLink) return false;
        const link = item.routerLink;
        const tree = link instanceof UrlTree ? link : this.router.createUrlTree(Array.isArray(link) ? link : [link], { queryParams: item.queryParams });
        return this.router.isActive(tree, { paths: 'exact', queryParams: 'subset', fragment: 'ignored', matrixParams: 'subset' });
    }
}
